import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ModalActionBar from 'components/modal-action-bar';
import { useAddMortgage } from './add-mortgage-hooks';

const useStyles = makeStyles((theme: any) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  title: {
    padding: theme.spacing(3, 3, 0),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2, 2, 0) },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      padding: theme.spacing(2),
    },
  },
}));

type AddmortgageModalProps = {
  open: boolean;
  onClose: () => void;
};

const AddMortgageModal: FC<AddmortgageModalProps> = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { methods, properties, createMortgageMutation, createMortgageReponse } = useAddMortgage(
    open,
    onClose
  );
  const { handleSubmit, control } = methods;
  const { t } = useTranslation();
  const isSubmitting = createMortgageReponse.isLoading;

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !isSubmitting) {
          onClose();
        }
      }}
      disableEscapeKeyDown={isSubmitting}
      fullWidth
      fullScreen={isMobile}
      maxWidth="md"
      scroll="paper"
    >
      <form
        className={classes.form}
        onSubmit={handleSubmit((data) => {
          createMortgageMutation({
            id: data.id,
            estateId: properties.length > 0 ? (data.estateId || null) : null,
            loan_number: data.loan_number ? data.loan_number.toString() : null,
          });
        })}
      >
        <DialogTitle className={classes.title}>
          <Typography variant="h6">
            {t('payoffs:add-new-mortgage')}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Controller
            control={control}
            name="loan_number"
            render={({ field }) => (
              <TextField
                label={t('payoffs:loan-number-if-known')}
                name="loan_number"
                onChange={field.onChange}
                handleBlur={field.onBlur}
                value={field.value}
                disabled={isSubmitting}
              />
            )}
          />
          {properties.length > 0 && (
            <Controller
              control={control}
              name="estateId"
              render={({ field: { value, ref, ...fieldProps } }) => (
                <SelectField
                  {...fieldProps}
                  inputRef={ref}
                  value={value || 'none'}
                  label={t('orders:property')}
                  dataKey="_id"
                  dataValue="_id"
                  dataText="address"
                  emptyValue={false}
                  disabled={isSubmitting}
                  data={[
                    { _id: 'none', address: t('payoffs:none-property') },
                    ...(properties?.map(
                      (property) => ({ _id: property._id, address: property.address.address })
                    ) || []),
                  ]}
                  handleChange={(e) => {
                    const selectedValue = e.target.value === 'none' ? null : e.target.value;
                    fieldProps.onChange(selectedValue);
                  }}
                  handleBlur={fieldProps.onBlur}
                />
              )}
            />
          )}
        </DialogContent>
        <ModalActionBar
          onCancel={onClose}
          submitText={t('common:save')}
          isSubmitting={isSubmitting}
          submitDisabled={!methods.formState.isValid}
        />
      </form>
    </Dialog>
  );
};

export default AddMortgageModal;
